import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Style.css";

export type DotChartDatum = {
  date: Date;
  value: number;
  meta?: Record<string, unknown>;
};

export type DotChartOverlaySeries = {
  data: DotChartDatum[];
  yScale?: "linear" | "log";
  className?: string;
};

type ChartPoint = {
  x: number;
  y: number;
  datum: DotChartDatum;
};

type Props = {
  data: DotChartDatum[];
  widthRem: number;
  heightRem: number;
  title: string;
  xAxisLabel: string;
  yAxisLabel: string;
  valueFormatter?: (value: number) => string;
  yAxisValueFormatter?: (value: number) => string;
  dateFormatter?: (date: Date) => string;
  yScale?: "linear" | "log";
  overlaySeries?: DotChartOverlaySeries[];
  pointClassNameProvider?: (datum: DotChartDatum, index: number) => string | undefined;
};

const DEFAULT_CHART_WIDTH = 900;
const DEFAULT_CHART_HEIGHT = 420;
const PADDING_TOP = 42;
const PADDING_BOTTOM = 62;
const Y_AXIS_LABEL_X = 10;

const formatDefaultDate = (date: Date) =>
  new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);

const formatDefaultValue = (value: number) =>
  new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(value);

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const DotChart = ({
  data,
  widthRem,
  heightRem,
  title,
  xAxisLabel,
  yAxisLabel,
  valueFormatter = formatDefaultValue,
  yAxisValueFormatter = valueFormatter,
  dateFormatter = formatDefaultDate,
  yScale = "linear",
  overlaySeries = [],
  pointClassNameProvider,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartSize, setChartSize] = useState({
    width: DEFAULT_CHART_WIDTH,
    height: DEFAULT_CHART_HEIGHT,
  });
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const updateSize = () => {
      if (!containerRef.current) {
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();
      setChartSize({
        width: Math.max(1, Math.floor(rect.width)),
        height: Math.max(260, Math.floor(rect.height)),
      });
    };

    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  const sortedData = useMemo(
    () => [...data].sort((a, b) => a.date.getTime() - b.date.getTime()),
    [data],
  );

  const chartWidth = chartSize.width;
  const chartHeight = chartSize.height;
  const paddingLeft = chartWidth < 520 ? 58 : 74;
  const paddingRight = chartWidth < 520 ? 14 : 22;

  const minX = sortedData[0]?.date.getTime() ?? 0;
  const maxX = sortedData[sortedData.length - 1]?.date.getTime() ?? 1;
  const minY = sortedData.length ? Math.min(...sortedData.map((d) => d.value)) : 0;
  const maxY = sortedData.length ? Math.max(...sortedData.map((d) => d.value)) : 1;
  const minYSafe = yScale === "log" ? Math.max(minY, 0.0000001) : minY;
  const maxYSafe = yScale === "log" ? Math.max(maxY, minYSafe * 1.01) : maxY;
  const yRange =
    yScale === "log"
      ? Math.log10(maxYSafe) - Math.log10(minYSafe) || 1
      : maxY - minY || 1;
  const xRange = maxX - minX || 1;

  const plotWidth = Math.max(1, chartWidth - paddingLeft - paddingRight);
  const plotHeight = Math.max(1, chartHeight - PADDING_TOP - PADDING_BOTTOM);

  const points: ChartPoint[] = sortedData.map((datum) => {
    const x = paddingLeft + ((datum.date.getTime() - minX) / xRange) * plotWidth;
    const normalizedY =
      yScale === "log"
        ? (Math.log10(Math.max(datum.value, minYSafe)) - Math.log10(minYSafe)) /
          yRange
        : (datum.value - minY) / yRange;
    const y = PADDING_TOP + (1 - normalizedY) * plotHeight;
    return { x, y, datum };
  });

  const overlayPaths = overlaySeries.map((series, seriesIndex) => {
    const overlayData = [...series.data]
      .filter((datum) => datum.date.getTime() >= minX && datum.date.getTime() <= maxX)
      .sort((a, b) => a.date.getTime() - b.date.getTime());
    if (!overlayData.length) {
      return null;
    }

    const overlayMinY = Math.min(...overlayData.map((d) => d.value));
    const overlayMaxY = Math.max(...overlayData.map((d) => d.value));
    const overlayScale = series.yScale ?? "linear";
    const overlayMinYSafe =
      overlayScale === "log" ? Math.max(overlayMinY, 0.0000001) : overlayMinY;
    const overlayMaxYSafe =
      overlayScale === "log"
        ? Math.max(overlayMaxY, overlayMinYSafe * 1.01)
        : overlayMaxY;
    const overlayRange =
      overlayScale === "log"
        ? Math.log10(overlayMaxYSafe) - Math.log10(overlayMinYSafe) || 1
        : overlayMaxYSafe - overlayMinYSafe || 1;

    const overlayPoints = overlayData.map((datum) => {
      const x = paddingLeft + ((datum.date.getTime() - minX) / xRange) * plotWidth;
      const normalizedY =
        overlayScale === "log"
          ? (Math.log10(Math.max(datum.value, overlayMinYSafe)) -
              Math.log10(overlayMinYSafe)) /
            overlayRange
          : (datum.value - overlayMinYSafe) / overlayRange;
      const y = PADDING_TOP + (1 - normalizedY) * plotHeight;
      return { x, y };
    });

    const d = overlayPoints
      .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
      .join(" ");

    return {
      key: `overlay-${seriesIndex}`,
      d,
      className: series.className ?? "dot-chart-overlay",
    };
  });

  const yMid =
    yScale === "log"
      ? Math.pow(10, Math.log10(minYSafe) + yRange / 2)
      : minY + yRange / 2;

  const setFromClientX = (clientX: number) => {
    if (!containerRef.current || points.length === 0) {
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const chartX = ((clientX - rect.left) / rect.width) * chartWidth;
    const clampedX = clamp(chartX, paddingLeft, paddingLeft + plotWidth);

    let nearestIndex = 0;
    let nearestDistance = Math.abs(points[0].x - clampedX);

    for (let i = 1; i < points.length; i += 1) {
      const distance = Math.abs(points[i].x - clampedX);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = i;
      }
    }

    setActiveIndex(nearestIndex);
  };

  const handlePointerDown: React.PointerEventHandler<HTMLDivElement> = (event) => {
    setIsActive(true);
    setFromClientX(event.clientX);
  };

  const handlePointerMove: React.PointerEventHandler<HTMLDivElement> = (event) => {
    if (!isActive && event.pointerType !== "mouse") {
      return;
    }

    setFromClientX(event.clientX);
  };

  const handlePointerEnter: React.PointerEventHandler<HTMLDivElement> = (event) => {
    setIsActive(true);
    setFromClientX(event.clientX);
  };

  const handlePointerLeave = () => {
    setIsActive(false);
    setActiveIndex(null);
  };

  const handlePointerUp = () => {
    setIsActive(false);
  };

  if (sortedData.length === 0) {
    return <div className="dot-chart-empty">No chart data available.</div>;
  }

  const activePoint = activeIndex === null ? null : points[activeIndex];

  return (
    <div
      className="dot-chart-root"
      style={{ width: "100%", maxWidth: `${widthRem}rem`, height: `${heightRem}rem` }}
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onPointerUp={handlePointerUp}
    >
      <svg
        width={chartWidth}
        height={chartHeight}
        role="img"
        aria-label={title}
      >
        <text x={chartWidth / 2} y={26} textAnchor="middle" className="dot-chart-title">
          {title}
        </text>

        <line
          x1={paddingLeft}
          y1={PADDING_TOP}
          x2={paddingLeft}
          y2={PADDING_TOP + plotHeight}
          className="dot-chart-axis"
        />
        <line
          x1={paddingLeft}
          y1={PADDING_TOP + plotHeight}
          x2={paddingLeft + plotWidth}
          y2={PADDING_TOP + plotHeight}
          className="dot-chart-axis"
        />

        <line
          x1={paddingLeft}
          y1={PADDING_TOP}
          x2={paddingLeft + plotWidth}
          y2={PADDING_TOP}
          className="dot-chart-range-line"
        />
        <line
          x1={paddingLeft}
          y1={PADDING_TOP + plotHeight / 2}
          x2={paddingLeft + plotWidth}
          y2={PADDING_TOP + plotHeight / 2}
          className="dot-chart-range-line"
        />
        <line
          x1={paddingLeft}
          y1={PADDING_TOP + plotHeight}
          x2={paddingLeft + plotWidth}
          y2={PADDING_TOP + plotHeight}
          className="dot-chart-range-line"
        />

        <text x={18} y={PADDING_TOP + 4} className="dot-chart-y-label">
          {yAxisValueFormatter(maxY)}
        </text>
        <text x={18} y={PADDING_TOP + plotHeight / 2 + 4} className="dot-chart-y-label">
          {yAxisValueFormatter(yMid)}
        </text>
        <text x={18} y={PADDING_TOP + plotHeight + 4} className="dot-chart-y-label">
          {yAxisValueFormatter(minY)}
        </text>

        <text
          x={chartWidth / 2}
          y={chartHeight - 14}
          textAnchor="middle"
          className="dot-chart-axis-title"
        >
          {xAxisLabel}
        </text>

        <text
          x={Y_AXIS_LABEL_X}
          y={chartHeight / 2}
          textAnchor="middle"
          className="dot-chart-axis-title"
          transform={`rotate(-90 ${Y_AXIS_LABEL_X} ${chartHeight / 2})`}
        >
          {yAxisLabel}
        </text>

        {overlayPaths.map((overlay) =>
          overlay ? (
            <path key={overlay.key} d={overlay.d} className={overlay.className} />
          ) : null,
        )}

        {points.map((point, index) => {
          const extraClass = pointClassNameProvider?.(point.datum, index) ?? "";
          return (
            <circle
              key={`dot-${index}`}
              cx={point.x}
              cy={point.y}
              r={3.2}
              className={`dot-chart-point ${extraClass}`.trim()}
            />
          );
        })}

        {activePoint ? (
          <>
            <line
              x1={activePoint.x}
              y1={PADDING_TOP}
              x2={activePoint.x}
              y2={PADDING_TOP + plotHeight}
              className="dot-chart-cursor"
            />
            <line
              x1={paddingLeft}
              y1={activePoint.y}
              x2={paddingLeft + plotWidth}
              y2={activePoint.y}
              className="dot-chart-cursor-horizontal"
            />
            <circle
              cx={activePoint.x}
              cy={activePoint.y}
              r={5}
              className="dot-chart-active-point"
            />
            <text
              x={paddingLeft + plotWidth - 8}
              y={clamp(activePoint.y - 8, PADDING_TOP + 12, PADDING_TOP + plotHeight - 4)}
              textAnchor="end"
              className="dot-chart-hover-value"
            >
              {valueFormatter(activePoint.datum.value)}
            </text>
            <text
              x={paddingLeft + plotWidth - 8}
              y={clamp(activePoint.y + 12, PADDING_TOP + 14, PADDING_TOP + plotHeight - 2)}
              textAnchor="end"
              className="dot-chart-hover-date"
            >
              {dateFormatter(activePoint.datum.date)}
            </text>
          </>
        ) : null}
      </svg>
    </div>
  );
};

export default DotChart;
